const DEFAULT_RETURN_PATH = "/";

/** Auth0 connection names this app links to directly, skipping the Universal Login IdP picker. */
export type AuthLoginConnection = "google-oauth2" | "linkedin";

export interface AuthLoginHrefOptions {
  /** Forwarded to Auth0 as `screen_hint=signup`: lands Universal Login on the sign-up
   * tab instead of its login-tab default. Standard Auth0 param, ignored harmlessly by
   * any tenant/connection that doesn't support it. */
  screenHint?: "signup";
  /** Forwarded to Auth0 as `connection=<name>`: sends the visitor straight to that
   * social IdP instead of Auth0's own connection picker. */
  connection?: AuthLoginConnection;
}

/**
 * Builds the login href with the `returnTo` the user should land back on after auth.
 * The `/auth/login` route is blocked from crawlers by `Disallow: /auth/` in robots.txt,
 * so `returnTo` in this URL is never exposed to search engines.
 */
export function buildAuthLoginHref(sanitizedPath: string, options?: AuthLoginHrefOptions): string {
  const screenHintParam = options?.screenHint ? `&screen_hint=${options.screenHint}` : "";
  const connectionParam = options?.connection ? `&connection=${options.connection}` : "";
  return `/auth/login?returnTo=${encodeURIComponent(sanitizedPath)}${screenHintParam}${connectionParam}`;
}

/**
 * Validates and sanitizes a path intended to be used as an internal redirect destination.
 * Rejects absolute URLs, protocol-relative URLs, and other potential open-redirect vectors.
 */
export function sanitizeReturnTo(input: string | null | undefined): string {
  if (typeof input !== "string") {
    return DEFAULT_RETURN_PATH;
  }

  const trimmed = input.trim();
  if (trimmed.length === 0) {
    return DEFAULT_RETURN_PATH;
  }

  if (!trimmed.startsWith("/")) {
    return DEFAULT_RETURN_PATH;
  }

  if (trimmed.startsWith("//")) {
    return DEFAULT_RETURN_PATH;
  }

  if (trimmed.includes("://")) {
    return DEFAULT_RETURN_PATH;
  }

  if (trimmed.includes("\0")) {
    return DEFAULT_RETURN_PATH;
  }

  return trimmed;
}

/** Social connections a signed-in user can attach to their account from the profile page. */
export const LINKABLE_CONNECTIONS: readonly AuthLoginConnection[] = ["linkedin", "google-oauth2"];

export function isLinkableConnection(value: unknown): value is AuthLoginConnection {
  return typeof value === "string" && (LINKABLE_CONNECTIONS as readonly string[]).includes(value);
}

/** Query params /auth/callback appends to returnTo after a link attempt (read by the profile page). */
export const LINK_RESULT_PARAM = "linked";
export const LINK_ERROR_PARAM = "link_error";
export type LinkErrorCode = "session" | "in_use" | "failed";

/**
 * Starts the "connect another sign-in method" flow for the signed-in user: a fresh Auth0 login
 * with that connection whose result /auth/callback attaches to the current account.
 */
export function buildAuthLinkHref(sanitizedPath: string, connection: AuthLoginConnection): string {
  return `/auth/link?connection=${connection}&returnTo=${encodeURIComponent(sanitizedPath)}`;
}
